// src/components/Header.jsx
import React from 'react';
import styled from 'styled-components';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/useAppStore';
import { useCurrencyStore } from '../store/useCurrencyStore';
import { useServices } from '../hooks/useServices';
import CurrencySwitcher from './controls/CurrencySwitcher';
import RegionSelect from './RegionSelect';
import MobileDrawer from './MobileDrawer';
import LeadModal from './quiz/LeadModal';

const HIDE_TO_DRAWER = 1000; // Точка полного скрытия меню и показа бургера
const LOGO_ICON_BP = 960; // ширина, ниже которой показываем компактный значок логотипа

// Breakpoints для адаптивного меню
const MENU_BREAKPOINTS = {
  // 16:10 разрешения
  WQXGA: 2560,    // 2560x1600
  WUXGA: 1920,    // 1920x1200  
  WSXGA: 1680,    // 1680x1050
  WXGA_PLUS: 1440, // 1440x900
  WXGA: 1280,     // 1280x800
  
  // Стандартные breakpoints
  XL: 1400,
  LG: 1200, 
  MD: 1000,
  SM: 900,
  XS: 768
};
const PHONE_TEL = process.env.REACT_APP_PHONE_TEL || '+74959729797';
const PHONE_DISPLAY = process.env.REACT_APP_PHONE_DISPLAY || '+7 (495) 972-97-97';
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP || '79309729797';
const TELEGRAM_URL = process.env.REACT_APP_TELEGRAM_URL || 'https://t.me/+79309729797';

/* ===== Fixed Header Bar ===== */
const Bar = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0; width: 100%;
  z-index: 1000;
  isolation: isolate; backdrop-filter: saturate(180%) blur(10px);
  background: ${({ theme }) => theme.glass};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 4px 18px rgba(0,0,0,.18);
  overflow-x: clip;
`;
const Spacer = styled.div` height: var(--header-h, 88px); `;

const Inner = styled.div`
  width:100%; padding:0 12px;
  @media (max-width:700px){
    padding:0 10px;
  }
  @media (max-width:400px){
    padding:0 8px;
  }
`;
const Wrap = styled.div`
  display:flex; align-items:center; gap:10px;
  min-height:72px; padding:8px 0;
  > * { min-width: 0; }
  @media (max-width:700px){
    gap:8px;
  }
  @media (max-width:400px){
    gap:6px;
  }
`;

const SelectSkin = styled.div`
  & select, & button.compact, & .compact {
    padding: 8px 10px; border-radius: 12px; font-size: 14px; line-height: 1.2;
    border: 1px solid ${({theme}) => theme.name === 'dark' ? 'rgba(255,255,255,.12)' : '#d7dbe6'};
    background: ${({theme}) => theme.name === 'dark' ? 'rgba(255,255,255,.05)' : '#fff'};
    color: inherit;
    @media (max-width:545px){
      min-width: 120px;
      width: 120px;
    }
    @media (max-width:475px){
      min-width: 100px;
      width: 100px;
    }
    @media (max-width:430px){
      min-width: 80px;
      font-size: 12px!important;
      width: 80px;
    }
  }
  & .currency-switcher, & .lang-toggle {
    padding: 2px; border-radius: 14px;
    background: ${({theme}) => theme.name === 'dark' ? 'transparent' : 'rgba(255,255,255,.6)'};
  }
`;

const Logo = styled(Link)`
  display:flex; align-items:center; flex:0 0 auto; white-space:nowrap;
  @media (max-width:700px){
    flex:0 0 auto;
  }
`;
const LogoPicture = styled.picture`
  display:block; height:64px;
  img{ height:64px; display:block; object-fit:contain; margin-bottom:-12px; }
  @media (max-width:700px){
    height:auto;
    img{
      width:60px;
      height:auto;
      margin-bottom:-5px;
    }
  }
  @media (max-width:400px){
    height:auto;
    img{
      width:50px;
      height:auto;
      margin-bottom:-4px;
    }
  }
  @media (max-width:340px){
    img{
      width:42px;
      margin-bottom:-3px;
    }
  }
`;
const Left = styled.div`
  display:flex; align-items:center; gap:8px; min-width:0; flex:1 1 auto;
  position: relative; /* Добавляем для правильного позиционирования */
  z-index: 5; /* Меньший z-index чем у Right */
  margin-right: 8px; /* Добавляем отступ справа чтобы не налезать на Right */
  
  @media (max-width:700px){
    flex:0 0 auto;
    gap:7px;
    margin-right: 6px;
  }
  @media (max-width:400px){
    flex:0 0 auto;
    gap:6px;
    margin-right: 4px;
  }
`;
const RegionBlock = styled.div`
  display:flex; align-items:center; gap:8px; flex:0 0 auto;
  @media(max-width:700px){ display:flex; } 

`;

const Nav = styled.nav`
  display:flex; align-items:center; gap:6px; margin-left:6px; flex-wrap:nowrap;
  flex:0 1 auto; min-width:0;
  a, .nav-btn{
    position: relative;
    padding:6px 9px; border-radius:10px; font-size:12px; white-space:nowrap; font-weight:500;
  }
  a::after, .nav-btn::after{
    content:""; position:absolute; left:8px; right:8px; bottom:2px;
    height:2px; background: transparent; border-radius:2px; transition: background .15s ease;
  }
  a:hover::after, .nav-btn:hover::after{ background: var(--primary); }
  a.active, .nav-btn.active{ background:${({theme})=>theme.card}; box-shadow:${({theme})=>theme.shadow}; }
  a.active::after, .nav-btn.active::after{ background: var(--primary); }
  @media (max-width:${HIDE_TO_DRAWER}px){ display:none; }
`;

// Адаптивная навигация - показывает разные элементы в зависимости от разрешения
const AdaptiveNav = styled.nav`
  display:flex; align-items:center; gap:6px; margin-left:6px; flex-wrap:nowrap;
  flex:0 1 auto; min-width:0;
  
  .nav-item {
    position: relative;
    padding:6px 9px; border-radius:10px; font-size:12px; white-space:nowrap; font-weight:500;
    text-decoration: none;
    color: inherit;
    background: transparent;
    border: none;
    cursor: pointer;
  }
  
  .nav-item::after {
    content:""; position:absolute; left:8px; right:8px; bottom:2px;
    height:2px; background: transparent; border-radius:2px; transition: background .15s ease;
  }
  
  .nav-item:hover::after { background: var(--primary); }
  .nav-item.active { background:${({theme})=>theme.card}; box-shadow:${({theme})=>theme.shadow}; }
  .nav-item.active::after { background: var(--primary); }
  
  /* Поэтапное скрытие элементов */
  .hideable-item {
    flex: 0 0 auto;
    
    /* Этап 1: Скрываем О нас на 1400px и меньше */
    @media (max-width: 1400px) {
      &.hide-step-1 { display: none; }
    }
    
    /* Этап 2: Скрываем Медиа на 1500px и меньше */
    @media (max-width: 1500px) {
      &.hide-step-2 { display: none; }
    }
    
    /* Этап 3: Скрываем Новости на 1600px и меньше */
    @media (max-width: 1600px) {
      &.hide-step-3 { display: none; }
    }
    
    /* Этап 4: Скрываем Блог на 1700px и меньше */
    @media (max-width: 1700px) {
      &.hide-step-4 { display: none; }
    }
    
    /* Этап 5: Скрываем Корпорацию и Районы на 1800px и меньше */
    @media (max-width: 1800px) {
      &.hide-step-5 { display: none; }
    }
    
    /* Этап 6: Скрываем Услуги на 1900px и меньше */
    @media (max-width: 1900px) {
      &.hide-step-6 { display: none; }
    }
    
    /* Этап 7: Скрываем Недвижимость на 1000px и меньше (последней) */
    @media (max-width: 1000px) {
      &.hide-step-7 { display: none; }
    }
  }
  
  /* Показываем AdaptiveNav на широких экранах */
  @media (max-width: 1710px) { display: none; }
`;

// Компактное меню для промежуточных разрешений (1000-1710px)
const CompactNav = styled.nav`
  display: none;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  flex: 0 1 auto;
  
  /* Показываем только на промежуточных разрешениях */
  @media (min-width: ${HIDE_TO_DRAWER + 1}px) and (max-width: 1710px) {
    display: flex;
  }
  
  .nav-item {
    position: relative;
    padding: 6px 9px;
    border-radius: 10px;
    font-size: 12px;
    white-space: nowrap;
    font-weight: 500;
    text-decoration: none;
    color: inherit;
    background: transparent;
    border: none;
    cursor: pointer;
  }
  
  .nav-item::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 2px;
    height: 2px;
    background: transparent;
    border-radius: 2px;
    transition: background .15s ease;
  }
  
  .nav-item:hover::after { background: var(--primary); }
  .nav-item.active { background: ${({theme})=>theme.card}; box-shadow: ${({theme})=>theme.shadow}; }
  .nav-item.active::after { background: var(--primary); }
`;

const Right = styled.div`
  display:flex; align-items:center; gap:8px; margin-left:auto; flex-wrap:nowrap; flex:0 0 auto;
  min-width: 0; /* Позволяет сжиматься */
  position: relative; /* Добавляем для правильного позиционирования */
  z-index: 10; /* Обеспечиваем что правая часть всегда поверх */
  
  /* Обеспечиваем блочное поведение для всех элементов */
  > * {
    flex: 0 0 auto; /* Не растягиваются и не сжимаются */
    display: block;
    position: relative;
  }
  
  @media (max-width:${HIDE_TO_DRAWER}px){
    .currency-switcher, .lang-toggle { display:none; }
  }
  @media (max-width:700px){
    gap:6px;
    margin-left:8px; /* Добавляем минимальный отступ от левой части */
    
    /* На мобильных скрываем некоторые элементы для экономии места */
    .theme-btn { display: none; }
  }
  @media (max-width:600px){
    gap:4px;
    margin-left:6px;
    
    /* Дополнительно скрываем элементы на очень маленьких экранах */
    .currency-switcher { display: none; }
  }
  @media (max-width:500px){
    gap:3px;
    margin-left:4px;
    
    /* На очень маленьких экранах показываем только самое необходимое */
    .lang-toggle { display: none; }
  }
  @media (max-width:400px){
    gap:2px;
    margin-left:2px;
  }
`;

const ServicesItem = styled.div`
  position:relative; flex:0 0 auto;
  &:hover .menu, &:focus-within .menu{ opacity:1; transform:translateY(0); pointer-events:auto; }
`;

/* триггер - кнопка, которая только открывает меню при hover,
   не ведет на страницу /services */
const ServicesTrigger = styled.button`
  background:transparent; border:1px solid transparent; cursor:pointer;
  padding:6px 9px; border-radius:10px; font-size:12px; color:inherit;
  display:flex; align-items:center; gap:6px; font-weight:500;
  &:hover{ background:${({theme})=>theme.card}; box-shadow:${({theme})=>theme.shadow}; }
`;

const Caret = styled.span`
  width:0; height:0; border-left:4px solid transparent; border-right:4px solid transparent;
  border-top:6px solid currentColor; opacity:.7;
`;

const Menu = styled.ul`
  position:absolute; top:100%; left:0; z-index:1000;
  min-width:260px; margin:0; padding:12px 8px 8px;
  list-style:none;
  background:var(--card); color:var(--fg); border:1px solid var(--bdr);
  border-radius:14px; box-shadow:var(--shadow); display:grid; gap:4px;
  opacity:0; transform:translateY(6px); pointer-events:none; transition:.18s ease;
  li a{
    width:100%; text-align:left;
    display:block; padding:9px 11px; border-radius:10px; white-space:nowrap;
  }
  li a:hover{ background: rgba(176,141,87,.12); }
`;

/* Icon buttons */
const IconBtn = styled.a`
  width:40px; height:40px; border-radius:12px;
  display:flex; align-items:center; justify-content:center;
  border:1px solid transparent; flex:0 0 auto;
  transition: transform .18s ease, box-shadow .18s ease;
  position: relative; /* Обеспечиваем правильное позиционирование */
  
  &:hover{ transform: translateY(-1px); }
  
  @media (max-width:800px){
    width:38px; height:38px; border-radius:11px;
  }
  @media (max-width:700px){
    width:36px; height:36px; border-radius:10px;
  }
  @media (max-width:600px){
    width:34px; height:34px; border-radius:10px;
  }
  @media (max-width:500px){
    width:32px; height:32px; border-radius:9px;
  }
  @media (max-width:400px){
    width:30px; height:30px; border-radius:8px;
  }
`;
const WaBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  
  svg{ 
    width:20px; height:20px; display:block; 
    @media (max-width:800px){ width:19px; height:19px; }
    @media (max-width:700px){ width:18px; height:18px; }
    @media (max-width:600px){ width:17px; height:17px; }
    @media (max-width:500px){ width:16px; height:16px; }
    @media (max-width:400px){ width:15px; height:15px; }
  }
  
  /* Скрываем на очень маленьких экранах для экономии места */
  @media (max-width:450px){
    display: none;
  }
`;

const TgBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  
  svg{ 
    width:20px; height:20px; display:block; 
    @media (max-width:800px){ width:19px; height:19px; }
    @media (max-width:700px){ width:18px; height:18px; }
    @media (max-width:600px){ width:17px; height:17px; }
    @media (max-width:500px){ width:16px; height:16px; }
    @media (max-width:400px){ width:15px; height:15px; }
  }
  
  /* Скрываем на очень маленьких экранах для экономии места */
  @media (max-width:400px){
    display: none;
  }
`;
const PhoneBtn = styled(IconBtn).attrs({ as:'button', type:'button' })`
  width:41px; height:41px; border-radius:16px;
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  
  svg{ 
    width:25px; height:25px; display:block; 
    @media (max-width:800px){ width:24px; height:24px; }
    @media (max-width:700px){ width:22px; height:22px; }
    @media (max-width:600px){ width:20px; height:20px; }
    @media (max-width:500px){ width:18px; height:18px; }
    @media (max-width:400px){ width:16px; height:16px; }
  }
  
  @media (max-width:800px){
    width:39px; height:39px; border-radius:15px;
  }
  @media (max-width:700px){
    width:37px; height:37px; border-radius:14px;
  }
  @media (max-width:600px){
    width:35px; height:35px; border-radius:13px;
  }
  @media (max-width:500px){
    width:33px; height:33px; border-radius:12px;
  }
  @media (max-width:400px){
    width:31px; height:31px; border-radius:11px;
  }
`;
const PhoneWrap = styled.div` position:relative; `;
const Pop = styled.div`
  position:absolute; top:calc(100% + 8px); right:0; background:var(--card); color:var(--fg);
  border:1px solid var(--bdr); border-radius:12px; box-shadow:var(--shadow); padding:10px 12px; white-space:nowrap; font-weight:700;
  a{ color:inherit; }
`;
const ThemeBtn = styled.button`
  width:48px; height:48px; border-radius:12px; border:1px solid var(--bdr);
  background: var(--card); color: var(--fg); display:flex; align-items:center; justify-content:center;
  box-shadow: var(--shadow); cursor:pointer; flex:0 0 auto; font-size:18px;
  position: relative; /* Обеспечиваем правильное позиционирование */
  
  @media (max-width:900px){
    width:44px; height:44px; font-size:16px;
  }
  @media (max-width:800px){
    width:42px; height:42px; font-size:15px;
  }
  @media (max-width:700px){
    display:none;
  }
`;
const FavBtn = styled(Link)`
  position: relative; width:40px; height:40px; border-radius:12px;
  display:flex; align-items:center; justify-content:center;
  border:1px solid var(--bdr); background: var(--card); box-shadow: var(--shadow);
  flex: 0 0 auto; /* Не сжимается */
  
  svg{ 
    width:19px; height:19px; display:block; 
    @media (max-width:800px){ width:18px; height:18px; }
    @media (max-width:700px){ width:17px; height:17px; }
    @media (max-width:600px){ width:16px; height:16px; }
    @media (max-width:500px){ width:15px; height:15px; }
    @media (max-width:400px){ width:14px; height:14px; }
  }
  
  @media (max-width:800px){
    width:38px; height:38px; border-radius:11px;
  }
  @media (max-width:700px){
    width:36px; height:36px; border-radius:10px;
  }
  @media (max-width:600px){
    width:34px; height:34px; border-radius:10px;
  }
  @media (max-width:500px){
    width:32px; height:32px; border-radius:9px;
  }
  @media (max-width:400px){
    width:30px; height:30px; border-radius:8px;
  }
`;
const FavBadge = styled.span`
  position:absolute; top:-6px; right:-6px; min-width:18px; height:18px;
  border-radius:9px; background: var(--primary); color: var(--primaryFg);
  font-size:11px; display:flex; align-items:center; justify-content:center; padding:0 4px;
`;
const Burger = styled.button`
  display:flex; width:40px; height:40px; border-radius:12px;
  border:1px solid var(--bdr); background: var(--card); color: var(--fg);
  align-items:center; justify-content:center; box-shadow: var(--shadow);
  font-size:20px; cursor:pointer; flex: 0 0 auto;
  
  @media (max-width:800px){
    width:38px; height:38px; border-radius:11px;
  }
  @media (max-width:700px){
    width:36px; height:36px; border-radius:10px;
  }
  @media (max-width:600px){
    width:34px; height:34px; border-radius:10px;
  }
  @media (max-width:500px){
    width:32px; height:32px; border-radius:9px;
  }
  @media (max-width:400px){
    width:30px; height:30px; border-radius:8px;
  }
`;

/* ===== Языковой дропдаун: показывает текущий язык, по клику открывает список ===== */
const LangDd = styled.div` position:relative; `;
const LangBtn = styled.button`
  display:inline-flex; align-items:center; justify-content:center; gap:6px;
  min-width:46px; padding:8px 12px; border-radius:14px; border:1px solid var(--bdr);
  background:${({theme})=>theme.name==='dark' ? 'rgba(30,32,38,.45)' : 'rgba(255,255,255,.85)'};
  color:inherit; font-weight:800; letter-spacing:.3px; cursor:pointer; box-shadow:var(--shadow);
`;
const LangMenu = styled.div`
  position:absolute; top:calc(100% + 6px); right:-16px; min-width:84px;
  background:var(--card); color:var(--fg); border:1px solid var(--bdr);
  border-radius:12px; box-shadow:var(--shadow); padding:6px; display:grid; gap:2px;
`;
const LangItem = styled.button`
  text-align:left; padding:8px 10px; border-radius:10px; border:0; background:transparent; color:inherit; cursor:pointer;
  font-weight:${p=>p.$active?800:600}; opacity:${p=>p.$active?.85:.9};
  &:hover{ background: rgba(176,141,87,.12); }
`;
function LangDropdown({ value, onChange, options = ['ru','en','zh'] }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(()=>{
    const onDoc = (e)=>{ if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onDoc);
    return ()=> document.removeEventListener('click', onDoc);
  },[]);
  const cur = (value || '').slice(0,2);
  
  // Функция для отображения языка
  const getLangDisplay = (code) => {
    const langMap = {
      'ru': 'RU',
      'en': 'EN', 
      'zh': '中'
    };
    return langMap[code.slice(0,2)] || code.toUpperCase();
  };
  
  return (
    <LangDd className="lang-toggle" ref={ref}>
      <LangBtn type="button" onClick={()=>setOpen(v=>!v)} aria-haspopup="listbox" aria-expanded={open}>
        {getLangDisplay(cur)}
      </LangBtn>
      {open && (
        <LangMenu role="listbox">
          {options.map(code => (
            <LangItem
              key={code}
              type="button"
              $active={code.slice(0,2) === cur}
              onClick={()=>{ onChange?.(code); setOpen(false); }}
            >
              {getLangDisplay(code)}
            </LangItem>
          ))}
        </LangMenu>
      )}
    </LangDd>
  );
}

export default function Header(){
  const { t, i18n } = useTranslation();
  const favCount = useAppStore(s => s.favorites.length);
  const themeName = useAppStore(s => s.theme);
  const setTheme = useAppStore(s => s.setTheme);
  const setLang = useAppStore(s => s.setLang);
  const lang = useAppStore(s => s.lang);
  const onLang = (lng) => { i18n.changeLanguage(lng); setLang(lng); };

  // Загрузка услуг из API
  const { services } = useServices();

  // Global currency conversion
  const currency = useCurrencyStore(s => s.currency);
  const setCurrency = useCurrencyStore(s => s.setCurrency);
  const [previousGlobalCurrency, setPreviousGlobalCurrency] = React.useState(currency);

  const [showPhone, setShowPhone] = React.useState(false);
  const [mOpen, setMOpen] = React.useState(false);
  const [openQuiz, setOpenQuiz] = React.useState(false);
  const [regionGateOpen, setRegionGateOpen] = React.useState(false);
  const regionSlug = useAppStore(s => s.regionSlug);

  // Состояние для отслеживания размера экрана
  const [screenWidth, setScreenWidth] = React.useState(window.innerWidth);

  // Global currency conversion effect
  React.useEffect(() => {
    if (!currency || currency === previousGlobalCurrency) {
      return;
    }

    // Trigger global currency conversion event
    const currencyChangeEvent = new CustomEvent('currencyChanged', {
      detail: {
        from: previousGlobalCurrency,
        to: currency,
        timestamp: Date.now()
      }
    });

    window.dispatchEvent(currencyChangeEvent);

    setPreviousGlobalCurrency(currency);
  }, [currency, previousGlobalCurrency]);

  const barRef = React.useRef(null);
  const updateHeaderHeight = React.useCallback(() => {
    const h = barRef.current?.offsetHeight || 88;
    document.documentElement.style.setProperty('--header-h', `${h}px`);
  }, []);
  
  React.useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      updateHeaderHeight();
    };
    
    updateHeaderHeight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateHeaderHeight, themeName, lang]);

  React.useEffect(() => {
    const close = () => setShowPhone(false);
    if (showPhone) document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showPhone]);

  const toggleTheme = () => setTheme(themeName === 'dark' ? 'light' : 'dark');

  const location = useLocation();
  const estatePaths = ['/search','/commercial','/secondary','/foreign','/suburban','/commercial-rent','/mansions','/parking','/rent'];
  const isEstateActive = estatePaths.some(p => location.pathname.startsWith(p));

  // Определяем текущий язык для логотипов
  const currentLang = i18n.language || lang || 'ru';
  
  // Функция для получения правильного логотипа
  const getLogoPath = (type = 'desktop') => {
    const logoMap = {
      desktop: {
        ru: "/ASTORIUS_PREMIUP_GORIZONT_RU.svg",
        en: "/ASTORIUS_PREMIUP_GORIZONT_ENG.svg", 
        zh: "/ASTORIUS_PREMIUP_GORIZONT_CHINA.svg"
      },
      mobile: {
        ru: "/ASTORIUS_FAVICON_RU.svg",
        en: "/ASTORIUS_FAVICON_ENG.svg",
        zh: "/ASTORIUS_FAVICON_CHINA.svg"
      }
    };
    
    return logoMap[type][currentLang] || logoMap[type]['ru'];
  };

  return (
    <>
      <Bar ref={barRef}>
        <SelectSkin>
          <Inner>
            <Wrap>
              <Left>
                <Logo to="/" aria-label="Astorius Home">
                  <LogoPicture>
                    {/* Мобильные логотипы (маленькие) */}
                    <source 
                      media={`(max-width:${LOGO_ICON_BP}px)`} 
                      srcSet={getLogoPath('mobile')} 
                    />
                    {/* Десктопные логотипы (большие) */}
                    <img 
                      src={getLogoPath('desktop')} 
                      alt="ASTORIUS" 
                    />
                  </LogoPicture>
                </Logo>

                <RegionBlock>
                  <RegionSelect includeAllOption={false} />
                  <button
                    className="btn-ghost compact"
                    onClick={() => setOpenQuiz(true)}
                    style={{fontSize:14}}
                  >
                    {t('pickObject')}
                  </button>
                </RegionBlock>

                <AdaptiveNav>
                  {/* О НАС - скрывается на 1400px и меньше */}
                  <NavLink 
                    to="/about" 
                    className="hideable-item hide-step-1 nav-item"
                  >
                    {t('header.servicesMenu.about')}
                  </NavLink>

                  {/* МЕДИА - скрывается на 1500px и меньше */}
                  <NavLink 
                    to="/socialmedia" 
                    className="hideable-item hide-step-2 nav-item"
                  >
                    {t('nav.media')}
                  </NavLink>
                  
                  {/* НОВОСТИ - скрывается на 1600px и меньше */}
                  <NavLink 
                    to="/news" 
                    className="hideable-item hide-step-3 nav-item"
                  >
                    {t('nav.news')}
                  </NavLink>
                  
                  {/* БЛОГ - скрывается на 1700px и меньше */}
                  <NavLink 
                    to="/blog" 
                    className="hideable-item hide-step-4 nav-item"
                  >
                    {t('nav.blog')}
                  </NavLink>
                  
                  {/* О НАС - скрывается на 1800px и меньше */}
                  <NavLink 
                    to="/about" 
                    className="hideable-item hide-step-5 nav-item"
                  >
                    {t('nav.corporation')}
                  </NavLink>

                  {/* РАЙОНЫ - скрываются на 1800px и меньше */}
                  <NavLink 
                    to="/districts" 
                    className="hideable-item hide-step-5 nav-item"
                  >
                    {t('districts')}
                  </NavLink>

                  {/* УСЛУГИ - скрываются на 1900px и меньше */}
                  <ServicesItem className="hideable-item hide-step-6">
                    <ServicesTrigger className="nav-item" aria-haspopup="true" aria-expanded="false">
                      {t('services')} <Caret />
                    </ServicesTrigger>
                    <Menu className="menu" role="menu">
                      {services.map(service => (
                        <li key={service.id}>
                          <Link to={service.link || `/services/${service.id}`}>{service.title}</Link>
                        </li>
                      ))}
                    </Menu>
                  </ServicesItem>

                  {/* НЕДВИЖИМОСТЬ - показывается последней, скрывается на 2000px и меньше */}
                  <ServicesItem className="hideable-item hide-step-7">
                    <button className={`nav-item ${isEstateActive ? 'active' : ''}`} style={{background:'transparent', border:0, cursor:'default'}}>
                      {t('nav.realty')} <Caret />
                    </button>
                    <Menu className="menu" role="menu">
                      <li><Link to="/search">{t('nav.newbuilds')}</Link></li>
                      <li><Link to="/commercial">{t('nav.commercial')}</Link></li>
                      <li><Link to="/secondary">{t('nav.secondary')}</Link></li>
                      <li><Link to="/foreign">{t('nav.foreign')}</Link></li>
                      <li><Link to="/suburban">{t('nav.suburban')}</Link></li>
                      <li><Link to="/commercial-rent">{t('nav.commercialRent')}</Link></li>
                      <li><Link to="/mansions">{t('nav.mansions')}</Link></li>
                      <li><Link to="/parking">{t('nav.parking')}</Link></li>
                      <li><Link to="/rent">{t('nav.rent')}</Link></li>
                    </Menu>
                  </ServicesItem>
                </AdaptiveNav>                {/* КОМПАКТНОЕ МЕНЮ для промежуточных разрешений (1000-1710px) */}
                <CompactNav>
                  {/* Показываем только самые важные элементы */}
                  
                  {/* Услуги - показываем если экран больше 1400px */}
                  {screenWidth > 1400 && (
                    <ServicesItem>
                      <ServicesTrigger className="nav-item" aria-haspopup="true" aria-expanded="false">
                        {t('services')} <Caret />
                      </ServicesTrigger>
                      <Menu className="menu" role="menu">
                        {services.map(service => (
                          <li key={service.id}>
                            <Link to={service.link || `/services/${service.id}`}>{service.title}</Link>
                          </li>
                        ))}
                      </Menu>
                    </ServicesItem>
                  )}

                  {/* Недвижимость - показываем если экран больше 1200px (остается дольше) */}
                  {screenWidth > 1200 && (
                    <ServicesItem>
                      <button className={`nav-item ${isEstateActive ? 'active' : ''}`} style={{background:'transparent', border:0, cursor:'default'}}>
                        {t('nav.realty')} <Caret />
                      </button>
                      <Menu className="menu" role="menu">
                        <li><Link to="/search">{t('nav.newbuilds')}</Link></li>
                        <li><Link to="/commercial">{t('nav.commercial')}</Link></li>
                        <li><Link to="/secondary">{t('nav.secondary')}</Link></li>
                        <li><Link to="/foreign">{t('nav.foreign')}</Link></li>
                        <li><Link to="/suburban">{t('nav.suburban')}</Link></li>
                        <li><Link to="/commercial-rent">{t('nav.commercialRent')}</Link></li>
                        <li><Link to="/mansions">{t('nav.mansions')}</Link></li>
                        <li><Link to="/parking">{t('nav.parking')}</Link></li>
                        <li><Link to="/rent">{t('nav.rent')}</Link></li>
                      </Menu>
                    </ServicesItem>
                  )}
                </CompactNav>
              </Left>

              <Right>
                {/* Порядок справа: избранное → телефон → Telegram → валюта → язык → тема */}
                <FavBtn to="/favorites" title={t('favorites')} aria-label={t('favorites')}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                    <path d="M12.1 21.35c-.1 0-.2 0-.3-.1C6.14 17.24 3 13.98 3 10.25 3 7.35 5.35 5 8.25 5c1.71 0 3.32.8 4.35 2.07A5.56 5.56 0 0 1 16.95 5C19.85 5 22.2 7.35 22.2 10.25c0 3.73-3.14 6.99-8.8 11.01-.1.07-.2.09-.3.09z"/>
                  </svg>
                  {!!favCount && <FavBadge>{favCount}</FavBadge>}
                </FavBtn>

                <PhoneWrap>
                  <PhoneBtn onClick={(e)=>{ e.stopPropagation(); setShowPhone(v=>!v); }} title={PHONE_DISPLAY} aria-label={PHONE_DISPLAY}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4.18 2h3a2 2 0 0 1 2 1.72c.12.81.37 1.59.72 2.32a2 2 0 0 1-.45 2.18L8.59 9.41a16 16 0 0 0 6 6l1.19-1.19a2 2 0 0 1 2.18-.45c.73.35 1.51.6 2.32.72A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </PhoneBtn>
                  {showPhone && (
                    <Pop onClick={(e)=>e.stopPropagation()}>
                      <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a>
                    </Pop>
                  )}
                </PhoneWrap>

                <TgBtn href={TELEGRAM_URL} target="_blank" rel="noreferrer" title="Telegram" aria-label="Telegram">
                  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </TgBtn>

                <div className="currency-switcher"><CurrencySwitcher /></div>
                <LangDropdown value={lang || i18n.language} onChange={onLang} options={['ru','en','zh']} />

                <ThemeBtn className="theme-btn" onClick={toggleTheme} title="Theme" aria-label="Toggle theme">
                  {themeName === 'dark' ? (
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{display:'block'}}>
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                  ) : (
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{display:'block'}}>
                      <circle cx="12" cy="12" r="4"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                  )}
                </ThemeBtn>

                {/* Бургер меню перенесено в правую часть */}
                <Burger aria-label="Open menu" onClick={()=>setMOpen(true)}>☰</Burger>
              </Right>
            </Wrap>
          </Inner>
        </SelectSkin>
      </Bar>

      <Spacer aria-hidden="true" />

      {/* Попап квиза */}
      <LeadModal open={openQuiz} onClose={() => setOpenQuiz(false)} />

      <MobileDrawer
        open={mOpen}
        onClose={()=>setMOpen(false)}
        onOpenQuiz={()=>setOpenQuiz(true)}
      />

      {/* Region Gate Modal */}
      {regionGateOpen && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex: 2000, display:'grid', placeItems:'center'}}>
          <div style={{background:'var(--card)', color:'var(--fg)', border:'1px solid var(--bdr)', borderRadius:16, boxShadow:'var(--shadow)', padding:18, width:'min(520px, 96vw)'}}>
            <h3 style={{margin:'0 0 10px'}}>{t('regions.selectRegion')}</h3>
            <p style={{opacity:.8, margin:'0 0 12px'}}>{i18n.language==='en' ? 'We’ll personalize listings and content for your region.' : 'Мы персонализируем объекты и контент под выбранный регион.'}</p>
            <div style={{display:'flex', gap:10, alignItems:'center'}}>
              <RegionSelect includeAllOption={false} />
              <button className="btn" onClick={()=> setRegionGateOpen(false)} disabled={!regionSlug}>
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
